import { Directive, ElementRef, forwardRef, HostListener } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { countryCd, countryPhoneNumber } from '../services/util.service';
import parsePhoneNumberFromString from 'libphonenumber-js';

@Directive({
  selector: '[appPhoneFormat]',
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => PhoneFormatValueAccessor),
    multi: true
  }]
})
export class PhoneFormatValueAccessor
  implements ControlValueAccessor {

  countryCode = countryPhoneNumber(countryCd);

  private onChange = (_: any) => { };
  private onTouched = () => { };

  constructor(private readonly el: ElementRef<HTMLInputElement>) { }

  writeValue(value: string): void {
    let inputVal = value
      ?.replace(/[^\d+]/g, '')   // remove non digits and +
      ?.replace(/(?!^)\+/g, '');
    if (!inputVal) {
      this.el.nativeElement.value = '';
      return;
    }
    let digits = inputVal;
    if (!inputVal.startsWith('+')) {
      inputVal = `${this.countryCode}${value}`;
    }

    try {
      const phone = parsePhoneNumberFromString(inputVal);

      if (!phone) {
        // Fallback: show raw cleaned value (do NOT truncate)
        this.el.nativeElement.value = this.format(digits);
        return;
      }

      // If we want to update the country code based on the input, update this.countryCode = `+${phone.countryCallingCode}`;
      digits = phone.nationalNumber;

      this.el.nativeElement.value = this.format(phone.nationalNumber);

    } catch {
      // Absolute fallback — no slicing
      this.el.nativeElement.value = digits;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  @HostListener('input', ['$event.target.value'])
  onInput(inputVal: string): void {
    let value = inputVal
      ?.replace(/[^\d+]/g, '')   // remove non digits and +
      ?.replace(/(?!^)\+/g, ''); // remove + if not at start
    if (!value) {
      this.el.nativeElement.value = '';
      this.onChange('');
      return;
    }

    let digits = value?.replace(/\D/g, '');

    try {
      if (!value.startsWith('+')) {
        value = `${this.countryCode}${value}`;
      }
      const phone = parsePhoneNumberFromString(value);

      if (phone) {
        // If we want to update the country code based on the input, update this.countryCode here. For now, we keep it fixed to the default country.
        digits = phone.nationalNumber;
      }
    } catch {
      // Ignore parse errors, fall back to default country
    }
    this.onChange(`${this.countryCode}${digits}`);
    this.el.nativeElement.value = this.format(digits);
  }

  @HostListener('blur')
  onBlur(): void {
    this.onTouched();
  }

  private format(digits: string): string {
    if (digits.length <= 3) {
      return digits;
    }
    if (digits.length <= 6) {
      return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    }
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  }
}
